import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiCloud,
  FiCode,
  FiZap,
  FiShield,
  FiTrendingUp,
  FiUsers,
  FiServer,
  FiGitBranch,
  FiLayers,
  FiArrowRight,
  FiCheck,
  FiPlay,
  FiStar,
  FiClock,
  FiGrid,
  FiFileText,
  FiFolder,
  FiFilter,
  FiSearch,
  FiShare2,
  FiLoader,
} from 'react-icons/fi';
import Sidebar from '../Dashboard/Sidebar';
import Header from '../Dashboard/Header';
import CapacityBar from '../Executions/CapacityBar';
import { useAuth } from '../../context/AuthContext';
import * as projectsApi from '../../api/projects';
import * as architecturesApi from '../../api/architectures';
import { getRecentActivity, type ActivityItem } from '../../api/activity';

type WorkspaceTab = 'recent' | 'favorites' | 'myApps' | 'external';

interface WorkspaceItem {
  id: string;
  name: string;
  type: string;
  openedAt: string;
  rawDate: string;
  location: string;
  approval?: string;
  confidentiality?: string;
  provider?: string;
  href?: string;
}

function formatRelativeDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffHours < 1) return "À l'instant";
  if (diffHours < 24) return `Il y a ${diffHours}h`;
  if (diffDays === 1) return 'Hier';
  if (diffDays < 7) return `Il y a ${diffDays} jours`;
  if (diffDays < 30) return `Il y a ${Math.floor(diffDays / 7)} sem.`;
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatCurrency(value: number): string {
  if (value === 0) return '—';
  if (value >= 1000) return `€${Math.round(value / 1000)}K`;
  return `€${Math.round(value)}`;
}

function providerColor(provider?: string): string {
  if (provider === 'azure') return '#0078D4';
  if (provider === 'aws') return '#FF9900';
  if (provider === 'gcp') return '#4285F4';
  if (provider === 'fabric') return '#742774';
  return '#6B7280';
}

const DemoPage = () => {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState<WorkspaceTab>('recent');
  const [searchFilter, setSearchFilter] = useState('');
  const [selectedProvider, setSelectedProvider] = useState<'azure' | 'aws' | 'gcp'>('azure');

  // Données réelles
  const [projects, setProjects] = useState<any[]>([]);
  const [architectures, setArchitectures] = useState<any[]>([]);
  const [recentActivity, setRecentActivity] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [projs, archs, activity] = await Promise.all([
          projectsApi.getProjects(),
          architecturesApi.getAllArchitectures(),
          getRecentActivity(30),
        ]);
        setProjects(projs);
        setArchitectures(archs);
        setRecentActivity(activity);
      } catch (err) {
        console.error('DemoPage load error:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // ── Stats KPIs ────────────────────────────────────────────────────────────
  const totalArchitectures = architectures.length;
  const totalResources = architectures.reduce((sum, a) => {
    const nodes = a.diagram_data?.nodes;
    if (!Array.isArray(nodes)) return sum;
    return sum + nodes.filter((n: any) => n.type !== 'group' && n.type !== 'annotation').length;
  }, 0);
  const totalSavings = architectures.reduce((sum, a) => sum + (Number(a.estimated_cost) || 0), 0);
  const totalProjects = projects.length;

  const stats = [
    {
      label: t('stats.architecturesCreated'),
      value: loading ? '…' : totalArchitectures.toLocaleString('fr-FR'),
      icon: <FiLayers />,
      color: 'text-blue-600',
    },
    {
      label: t('stats.resourcesDeployed'),
      value: loading ? '…' : (totalResources > 0 ? totalResources.toLocaleString('fr-FR') : projects.reduce((s, p) => s + parseInt(p.architectures_count || 0), 0).toLocaleString('fr-FR')),
      icon: <FiServer />,
      color: 'text-green-600',
    },
    {
      label: t('stats.savingsRealized'),
      value: loading ? '…' : formatCurrency(totalSavings),
      icon: <FiTrendingUp />,
      color: 'text-purple-600',
    },
    {
      label: 'Projets actifs',
      value: loading ? '…' : totalProjects.toLocaleString('fr-FR'),
      icon: <FiUsers />,
      color: 'text-orange-600',
    },
  ];

  // ── Workspace items (données réelles) ─────────────────────────────────────
  const orgName = user?.organization_name || 'GenIaC';

  const projectItems: WorkspaceItem[] = projects.map(p => ({
    id: `proj-${p.id}`,
    name: p.name,
    type: 'Projet',
    openedAt: formatRelativeDate(p.updated_at),
    rawDate: p.updated_at,
    location: orgName,
    provider: p.provider,
    href: `/projects/${p.id}`,
  }));

  const archItems: WorkspaceItem[] = architectures.map(a => ({
    id: `arch-${a.id}`,
    name: a.name,
    type: 'Architecture',
    openedAt: formatRelativeDate(a.updated_at),
    rawDate: a.updated_at,
    location: a.project_name || '—',
    provider: a.provider,
    href: `/projects/${a.project_id}`,
  }));

  const favoriteItems: WorkspaceItem[] = archItems.filter(
    (_, i) => architectures[i]?.is_favorite
  );

  // Onglet "Récent" : items consultés par l'utilisateur (tracés), ou fallback sur updated_at
  const recentItems: WorkspaceItem[] = recentActivity.length > 0
    ? recentActivity.map(a => ({
        id: `act-${a.resource_type}-${a.resource_id}`,
        name: a.name,
        type: a.resource_type === 'architecture' ? 'Architecture' : 'Projet',
        openedAt: formatRelativeDate(a.accessed_at),
        rawDate: a.accessed_at,
        location: a.project_name || orgName,
        provider: a.provider,
        href: a.resource_type === 'architecture'
          ? `/projects/${a.project_id}`
          : `/projects/${a.resource_id}`,
      }))
    // fallback : merge projets + architectures triés par dernière modification
    : [...projectItems, ...archItems]
        .sort((a, b) => new Date(b.rawDate).getTime() - new Date(a.rawDate).getTime())
        .slice(0, 15);

  const getTabItems = (): WorkspaceItem[] => {
    switch (activeTab) {
      case 'recent': return recentItems;
      case 'favorites': return favoriteItems;
      case 'myApps': return archItems;
      case 'external': return [];
      default: return recentItems;
    }
  };

  const filteredWorkspaces = getTabItems().filter(item =>
    !searchFilter ||
    item.name.toLowerCase().includes(searchFilter.toLowerCase()) ||
    item.type.toLowerCase().includes(searchFilter.toLowerCase()) ||
    item.location.toLowerCase().includes(searchFilter.toLowerCase())
  );

  // ── UI helpers ────────────────────────────────────────────────────────────
  const getItemIcon = (item: WorkspaceItem) => {
    const color = providerColor(item.provider);
    if (item.type === 'Architecture') return <FiLayers style={{ color }} />;
    if (item.type === 'Projet') return <FiFolder style={{ color: '#F59E0B' }} />;
    if (item.type === 'Rapport') return <FiFileText style={{ color: '#8B5CF6' }} />;
    return <FiGrid style={{ color: '#6B7280' }} />;
  };

  const features = [
    { icon: <FiCloud />, title: t('features.multiCloud.title'), description: t('features.multiCloud.description'), gradient: 'from-blue-500 to-blue-600' },
    { icon: <FiCode />, title: t('features.iac.title'), description: t('features.iac.description'), gradient: 'from-green-500 to-green-600' },
    { icon: <FiZap />, title: t('features.fastDeployment.title'), description: t('features.fastDeployment.description'), gradient: 'from-yellow-500 to-orange-600' },
    { icon: <FiShield />, title: t('features.security.title'), description: t('features.security.description'), gradient: 'from-red-500 to-pink-600' },
  ];

  const quickActions = [
    { title: t('quickActions.createArchitecture.title'), description: t('quickActions.createArchitecture.description'), icon: <FiLayers />, action: () => navigate('/getting-started'), color: 'bg-blue-600 hover:bg-blue-700' },
    { title: t('quickActions.exploreTemplates.title'), description: t('quickActions.exploreTemplates.description', { count: 12 }), icon: <FiGitBranch />, action: () => navigate('/templates'), color: 'bg-green-600 hover:bg-green-700' },
    { title: t('quickActions.viewProjects.title'), description: t('quickActions.viewProjects.description'), icon: <FiServer />, action: () => navigate('/projects'), color: 'bg-purple-600 hover:bg-purple-700' },
  ];

  const providerResources = {
    azure: ['Virtual Machines', 'Azure SQL', 'Storage Account', 'AKS', 'Key Vault'],
    aws: ['EC2', 'RDS', 'S3', 'EKS', 'Lambda'],
    gcp: ['Compute Engine', 'Cloud SQL', 'Cloud Storage', 'GKE', 'Cloud Functions'],
  };

  const tabs = [
    { id: 'recent' as WorkspaceTab, label: t('workspaces.tabs.recent'), icon: <FiClock /> },
    { id: 'favorites' as WorkspaceTab, label: t('workspaces.tabs.favorites'), icon: <FiStar /> },
    { id: 'myApps' as WorkspaceTab, label: t('workspaces.tabs.myApps'), icon: <FiGrid /> },
    { id: 'external' as WorkspaceTab, label: t('workspaces.tabs.external'), icon: <FiShare2 /> },
  ];

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />

      <div className="flex-1 flex flex-col">
        <Header />

        <main className="flex-1 p-8 overflow-y-auto">
          {/* Hero Section */}
          <div className="mb-8">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white shadow-lg">
              <h1 className="text-4xl font-bold mb-2">{t('hero.title')}</h1>
              {user && (
                <p className="text-blue-200 text-sm mb-3">
                  Connecté en tant que <strong>{user.full_name}</strong> · {orgName}
                </p>
              )}
              <p className="text-xl text-blue-100 mb-6">{t('hero.subtitle')}</p>
              <div className="flex space-x-4">
                <button
                  onClick={() => navigate('/getting-started')}
                  className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition flex items-center space-x-2"
                >
                  <FiPlay />
                  <span>{t('hero.startNow')}</span>
                </button>
                <button
                  onClick={() => navigate('/templates')}
                  className="bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-800 transition flex items-center space-x-2"
                >
                  <FiLayers />
                  <span>{t('hero.exploreTemplates')}</span>
                </button>
              </div>
            </div>
          </div>

          {/* Capacité d'exécution */}
          <div className="mb-6">
            <CapacityBar compact />
          </div>

          {/* Stats KPIs — données réelles */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {stats.map((stat, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition">
                <div className="flex items-center justify-between mb-4">
                  <div className={`text-3xl ${stat.color}`}>{stat.icon}</div>
                  {loading ? (
                    <FiLoader className="text-gray-400 animate-spin" size={22} />
                  ) : (
                    <span className="text-2xl font-bold text-gray-900">{stat.value}</span>
                  )}
                </div>
                <p className="text-gray-600 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Workspace Management Section — données réelles */}
          <div className="mb-8 bg-white rounded-xl shadow-md">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">{t('workspaces.title')}</h2>
                {loading && <FiLoader className="text-blue-500 animate-spin" size={20} />}
              </div>

              {/* Tabs */}
              <div className="flex border-b border-gray-200 mb-6 overflow-x-auto">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 px-4 py-3 font-medium transition whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-b-2 border-blue-600 text-blue-600'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <span>{tab.icon}</span>
                    <span>{tab.label}</span>
                    {activeTab === tab.id && !loading && (
                      <span className="ml-1 bg-blue-100 text-blue-700 text-xs font-bold px-2 py-0.5 rounded-full">
                        {getTabItems().length}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* Search */}
              <div className="flex items-center space-x-4 mb-6">
                <div className="flex-1 relative">
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder={t('workspaces.searchPlaceholder')}
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                  />
                </div>
                <button className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-gray-700">
                  <FiFilter />
                  <span>{t('workspaces.filters')}</span>
                </button>
              </div>

              {/* Workspace Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">{t('workspaces.table.name')}</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">{t('workspaces.table.type')}</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">{t('workspaces.table.opened')}</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">{t('workspaces.table.location')}</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Provider</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      Array.from({ length: 5 }).map((_, i) => (
                        <tr key={i} className="border-b border-gray-100">
                          <td colSpan={5} className="py-4 px-4">
                            <div className="h-4 bg-gray-100 rounded animate-pulse w-3/4" />
                          </td>
                        </tr>
                      ))
                    ) : filteredWorkspaces.length > 0 ? (
                      filteredWorkspaces.map((item) => (
                        <tr
                          key={item.id}
                          className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition"
                          onClick={() => item.href && navigate(item.href)}
                        >
                          <td className="py-4 px-4">
                            <div className="flex items-center space-x-3">
                              <span className="text-xl">{getItemIcon(item)}</span>
                              <span className="font-medium text-gray-900">{item.name}</span>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              item.type === 'Architecture' ? 'bg-blue-50 text-blue-700' :
                              item.type === 'Projet' ? 'bg-amber-50 text-amber-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {item.type}
                            </span>
                          </td>
                          <td className="py-4 px-4 text-gray-600 text-sm">{item.openedAt}</td>
                          <td className="py-4 px-4 text-gray-600 text-sm">{item.location}</td>
                          <td className="py-4 px-4">
                            {item.provider ? (
                              <span
                                className="px-2 py-1 rounded text-xs font-semibold uppercase"
                                style={{
                                  background: `${providerColor(item.provider)}15`,
                                  color: providerColor(item.provider),
                                }}
                              >
                                {item.provider}
                              </span>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="py-12 text-center">
                          <FiFolder className="mx-auto text-gray-300 mb-3" size={40} />
                          <p className="text-gray-500">{t('workspaces.noWorkspaces')}</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {!loading && filteredWorkspaces.length > 0 && (
                <div className="mt-4 text-sm text-gray-500">
                  {filteredWorkspaces.length} élément{filteredWorkspaces.length > 1 ? 's' : ''} affiché{filteredWorkspaces.length > 1 ? 's' : ''}
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('quickActions.title')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {quickActions.map((action, index) => (
                <button
                  key={index}
                  onClick={action.action}
                  className={`${action.color} text-white rounded-xl p-6 text-left transition transform hover:scale-105 shadow-lg`}
                >
                  <div className="text-3xl mb-4">{action.icon}</div>
                  <h3 className="text-xl font-bold mb-2">{action.title}</h3>
                  <p className="text-sm text-white/90">{action.description}</p>
                  <div className="mt-4 flex items-center text-sm font-semibold">
                    <span>{t('quickActions.start')}</span>
                    <FiArrowRight className="ml-2" />
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Features Grid */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('features.title')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {features.map((feature, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition">
                  <div className={`inline-flex items-center justify-center w-12 h-12 rounded-lg bg-gradient-to-r ${feature.gradient} text-white text-2xl mb-4`}>
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                  <div className="mt-4 flex items-center text-blue-600 font-semibold text-sm">
                    <span>{t('features.learnMore')}</span>
                    <FiArrowRight className="ml-2" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Cloud Provider Section */}
          <div className="bg-white rounded-xl p-8 shadow-md mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('cloudResources.title')}</h2>
            <div className="flex space-x-4 mb-6 border-b border-gray-200">
              {(['azure', 'aws', 'gcp'] as const).map((provider) => (
                <button
                  key={provider}
                  onClick={() => setSelectedProvider(provider)}
                  className={`pb-4 px-4 font-semibold transition uppercase text-sm ${
                    selectedProvider === provider
                      ? 'border-b-2 border-blue-600 text-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {provider}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {providerResources[selectedProvider].map((resource, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-4 text-center hover:bg-gray-100 transition cursor-pointer">
                  <div className="text-blue-600 text-2xl mb-2"><FiServer className="mx-auto" /></div>
                  <p className="text-sm font-medium text-gray-900">{resource}</p>
                </div>
              ))}
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-4 text-center flex items-center justify-center">
                <p className="text-blue-600 font-semibold text-sm">{t('cloudResources.more', { count: selectedProvider === 'azure' ? 77 : 50 })}</p>
              </div>
            </div>
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-700">
                <FiCheck className="inline text-green-600 mr-2" />
                {t('cloudResources.validationMessage')}
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-8 text-white text-center">
            <h2 className="text-3xl font-bold mb-4">{t('cta.title')}</h2>
            <p className="text-lg text-purple-100 mb-6">{t('cta.subtitle')}</p>
            <button
              onClick={() => navigate('/getting-started')}
              className="bg-white text-purple-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-purple-50 transition inline-flex items-center space-x-2"
            >
              <span>{t('cta.button')}</span>
              <FiArrowRight />
            </button>
          </div>
        </main>
      </div>
    </div>
  );
};

export default DemoPage;
